/**
 * Aura Scan report — render contract from the backend (APP-1400).
 * Native in-app report: no PDF, no email. Content arrives on `GET /v1/reports/me`.
 */

export type Zodiac =
  | "aries"
  | "taurus"
  | "gemini"
  | "cancer"
  | "leo"
  | "virgo"
  | "libra"
  | "scorpio"
  | "sagittarius"
  | "capricorn"
  | "aquarius"
  | "pisces"

export type Planet =
  | "sun"
  | "moon"
  | "mercury"
  | "venus"
  | "mars"
  | "jupiter"
  | "saturn"
  | "uranus"
  | "neptune"
  | "pluto"

export type ArchetypeId =
  | "wildfire-beacon"
  | "rootbinder"
  | "skyline-voice"
  | "moonwell"
  | "prism-hearth"
  | "quiet-compass"
  | "nightbloom"
  | "deep-current"
  | "northlight"
  | "ember-weaver"
  | "stormrider"
  | "tidal-guardian"

export type SensitivityLevel = "low" | "moderate" | "high" | "very-high"

export type MoonPrecision = "exact" | "approximate" | "estimated"

export interface AuraTrait {
  title: string
  description: string
}

export interface AuraColor {
  /** Human-readable, e.g. "Moonlit aqua". */
  name: string
  /** Light hex for dark backgrounds, e.g. "#5FD3E6". */
  hex: string
  description: string
}

export interface SensitivityScale {
  /** Integer 0..100. */
  score: number
  level: SensitivityLevel
  description: string
}

export interface AuraScanReport {
  subject: {
    name: string | null
    birthDate: string
    birthTime: string | null
  }
  signs: {
    sunSign: Zodiac
    moonSign: Zodiac
    rulingPlanet: Planet
    moonPrecision: MoonPrecision
  }
  archetype: {
    id: ArchetypeId
    name: string
    oneLiner: string
  }
  signature: {
    tags: string[]
    primaryColor: AuraColor
    secondaryColor: AuraColor
    description: string
  }
  sensitivity: {
    natureAttunement: SensitivityScale
    energySensitivity: SensitivityScale
    intuitiveReception: SensitivityScale
  }
  superpowers: AuraTrait[]
  growthEdges: AuraTrait[]
  powerChannels: AuraTrait[]
  ritual: {
    title: string
    when: string
    intro: string
    steps: string[]
  }
}

/** Public `POST /v1/reports/aura-scan` user snapshot (quiz / additional-offers). */
export interface AuraScanPublicUser {
  name: string | null
  gender: "male" | "female" | "other"
  /** `YYYY-MM-DD`. */
  birthDate: string
  /** 24h `HH:mm`, or null when unknown. */
  birthTime?: string | null
  birthPlace?: {
    label: string
    lat: number
    lon: number
  } | null
}

export function isAuraScanReport(content: unknown): content is AuraScanReport {
  return Boolean(
    content &&
      typeof content === "object" &&
      "archetype" in content &&
      "signature" in content &&
      "sensitivity" in content,
  )
}
